import { Command } from "cmdk";
import { Building2, Handshake, User } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router";
import { useSearch } from "../lib/queries";
import { int } from "../lib/format";
import { StageChip } from "./chips";

/** ⌘K: jump to any provider, person or deal. */
export function CommandPalette({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const [q, setQ] = useState("");
  const { data, isFetching } = useSearch(q);
  const navigate = useNavigate();
  const go = (to: string) => {
    onOpenChange(false);
    setQ("");
    navigate(to);
  };
  const empty = q.trim().length >= 2 && data && !data.organisations.length && !data.contacts.length && !data.deals.length;

  return (
    <Command.Dialog
      open={open}
      onOpenChange={onOpenChange}
      label="Search the hub"
      shouldFilter={false}
      overlayClassName="fixed inset-0 z-40 bg-moss/35 backdrop-blur-[2px] animate-fade-in"
      contentClassName="fixed top-[12vh] left-1/2 z-50 w-[calc(100vw-32px)] max-w-xl -translate-x-1/2 overflow-hidden rounded-2xl bg-paper shadow-[var(--shadow-pop)] animate-pop-in"
    >
      <Command.Input
        value={q}
        onValueChange={setQ}
        placeholder="Search providers, people and deals…"
        className="h-14 w-full border-b border-line-soft px-5 text-[15px] text-ink outline-none placeholder:text-subtle"
      />
      <Command.List className="scroll-thin max-h-[56vh] overflow-y-auto p-2">
        {q.trim().length < 2 && <div className="px-3 py-6 text-center text-sm text-muted">Type at least two letters. Deal refs like D-0042 work too.</div>}
        {isFetching && !data && <div className="px-3 py-6 text-center text-sm text-muted">Searching…</div>}
        {empty && <div className="px-3 py-6 text-center text-sm text-muted">Nothing found for “{q}”.</div>}
        {data && data.organisations.length > 0 && (
          <Command.Group heading="Providers and partners" className="[&_[cmdk-group-heading]]:eyebrow [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-1.5">
            {data.organisations.map((o) => (
              <Command.Item key={o.id} value={`org-${o.id}`} onSelect={() => go(o.registry === "partner" ? `/partners/${o.id}` : `/prospects/${o.id}`)} className={ITEM}>
                <Building2 className="size-4 text-sage-300" />
                <span className="flex-1 truncate font-semibold text-ink">{o.name}</span>
                <span className="text-xs text-muted">
                  {o.town ? `${o.town} · ` : ""}
                  {int(o.siteCount)} site{o.siteCount === 1 ? "" : "s"}
                  {o.registry === "partner" ? " · Partner" : o.isCustomer ? " · Customer" : ""}
                </span>
              </Command.Item>
            ))}
          </Command.Group>
        )}
        {data && data.contacts.length > 0 && (
          <Command.Group heading="People" className="[&_[cmdk-group-heading]]:eyebrow [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-1.5">
            {data.contacts.map((c) => (
              <Command.Item key={c.id} value={`contact-${c.id}`} onSelect={() => go(`/contacts/${c.id}`)} className={ITEM}>
                <User className="size-4 text-sage-300" />
                <span className="flex-1 truncate font-semibold text-ink">{c.name}</span>
                <span className="truncate text-xs text-muted">{c.organisationName ?? c.email}</span>
              </Command.Item>
            ))}
          </Command.Group>
        )}
        {data && data.deals.length > 0 && (
          <Command.Group heading="Deals" className="[&_[cmdk-group-heading]]:eyebrow [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-1.5">
            {data.deals.map((d) => (
              <Command.Item key={d.id} value={`deal-${d.id}`} onSelect={() => go(`/deals/${d.id}`)} className={ITEM}>
                <Handshake className="size-4 text-sage-300" />
                <span className="num text-xs font-bold text-muted">{d.ref}</span>
                <span className="flex-1 truncate font-semibold text-ink">{d.organisationName}</span>
                <StageChip stageId={d.stageId} />
              </Command.Item>
            ))}
          </Command.Group>
        )}
      </Command.List>
    </Command.Dialog>
  );
}

const ITEM = "flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-sm data-[selected=true]:bg-sage-50";
